/**
 * Input Component
 *
 * A reusable form input component with:
 * - Label support
 * - Error message display
 * - Confidence-based highlighting (for AI-extracted fields)
 * - Integration with react-hook-form
 *
 * The confidence feature is key for the HITL workflow:
 * - If confidence < 0.8, the input gets a yellow border
 * - This alerts the user to review fields that AI wasn't confident about
 */

import { forwardRef } from 'react';
import clsx from 'clsx';

/**
 * Input Component
 *
 * @param {Object} props - Component props
 * @param {string} [props.label] - Label text displayed above input
 * @param {Object} [props.error] - Error object from react-hook-form
 * @param {number} [props.confidence] - AI confidence score (0-1)
 * @param {string} [props.type='text'] - Input type (text, email, password, etc.)
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref for react-hook-form
 *
 * @example
 * // Basic usage
 * <Input label="Email" type="email" {...register('email')} />
 *
 * @example
 * // With confidence highlighting (for AI-extracted data)
 * <Input
 *   label="PO Number"
 *   confidence={0.65}  // Will show yellow border
 *   {...register('header.poNumber')}
 * />
 *
 * @example
 * // With error handling
 * <Input
 *   label="Email"
 *   error={errors.email}
 *   {...register('email', { required: 'Email is required' })}
 * />
 */
/**
 * Severity-to-color map for issue indicators and tooltip borders
 */
const SEVERITY_COLORS = {
  error:   { border: 'border-l-red-500',   text: 'text-red-600',   icon: 'text-red-500' },
  warning: { border: 'border-l-amber-500', text: 'text-amber-600', icon: 'text-amber-500' },
  info:    { border: 'border-l-blue-500',  text: 'text-blue-600',  icon: 'text-blue-500' },
};

/**
 * Title-case a snake_case type string (e.g. "missing_field" → "Missing Field")
 */
function formatIssueType(type) {
  if (!type) return 'Issue';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const Input = forwardRef(({
  label,
  error,
  confidence,
  issues = [],
  type = 'text',
  className,
  ...props
}, ref) => {
  // Check if this field has low confidence from AI extraction
  // Fields with confidence < 0.8 should be highlighted for human review
  const hasConfidence = confidence !== undefined;
  const hasLowConfidence = hasConfidence && confidence < 0.8;
  const hasIssues = issues.length > 0;

  // Pick the worst severity from the issues array for the indicator color
  const worstSeverity = hasIssues
    ? (issues.some(i => i.severity === 'error') ? 'error'
      : issues.some(i => i.severity === 'warning') ? 'warning' : 'info')
    : null;

  return (
    <div className="mb-4">
      {/* Label with optional confidence indicator and issues icon */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <span className="inline-flex items-center gap-1">
            {label}

            {/* Show confidence percentage — green if high, yellow if low */}
            {hasConfidence && (
              <span className={`text-xs font-normal ${hasLowConfidence ? 'text-yellow-600' : 'text-green-600'}`}>
                ({Math.round(confidence * 100)}%)
              </span>
            )}

            {/* Issues indicator — warning triangle with hover tooltip */}
            {hasIssues && (
              <span className="relative group/issues inline-flex">
                <svg
                  className={`w-3.5 h-3.5 ${SEVERITY_COLORS[worstSeverity]?.icon || 'text-amber-500'} cursor-help`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>

                {/* Tooltip card — appears on hover */}
                <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/issues:block w-64">
                  <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                    {issues.map((issue, idx) => (
                      <div
                        key={idx}
                        className={`px-3 py-2 border-l-4 ${SEVERITY_COLORS[issue.severity]?.border || 'border-l-gray-300'} ${idx > 0 ? 'border-t border-gray-100' : ''}`}
                      >
                        <p className={`text-xs font-semibold ${SEVERITY_COLORS[issue.severity]?.text || 'text-gray-600'}`}>
                          {formatIssueType(issue.type)}
                        </p>
                        {issue.detail && (
                          <p className="text-xs text-gray-600 mt-0.5">{issue.detail}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </span>
            )}
          </span>
        </label>
      )}

      {/* Input field with dynamic styling */}
      <input
        ref={ref}
        type={type}
        className={clsx(
          // Base styles
          'w-full px-3 py-2 border rounded-md shadow-sm transition-colors',
          'focus:outline-none focus:ring-2',

          // Normal state (no error, high confidence, no issues)
          !error && !hasLowConfidence && !hasIssues && [
            'border-gray-300',
            'focus:ring-blue-500 focus:border-blue-500'
          ],

          // Error state (validation failed)
          error && [
            'border-red-500',
            'focus:ring-red-500 focus:border-red-500',
            'bg-red-50'
          ],

          // Low confidence or issues state (AI wasn't sure or flagged a problem)
          // Yellow highlight to draw user's attention
          (hasLowConfidence || hasIssues) && !error && [
            'border-amber-400 border-2',
            'bg-amber-50',
            'focus:ring-amber-500 focus:border-amber-500'
          ],

          // Additional custom classes
          className
        )}
        {...props}
      />

      {/* Error message display */}
      {error && (
        <p className="mt-1 text-sm text-red-600">
          {error.message}
        </p>
      )}
    </div>
  );
});

// Display name for React DevTools
Input.displayName = 'Input';

export default Input;
